// @flow
import * as React from 'react';

import { Plus } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Loader, View } from 'reshaped';

import { selectIsSamlUser } from '@gandi/authentication';

import { selectCartReselleeID } from '@gandi/cart';
import {
  fetchOrganizationsList,
  selectOrganizationsList,
  organizationsResource,
} from '@gandi/redux-organization';
import { Link } from '@gandi/reshaped';
import { useTranslator } from '@gandi/translate';

import { useCartSummaryContext } from 'src/Layout/context/CartSummary';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { SearchField } from '../../SearchField';
import { SelectContactForm } from '../../SelectContactForm';
import { useApplyOrgaProductOwner } from '../../hooks/useApplyOrgaProductOwner';
import type { Props } from '../../hooks/useModalType';
import { useSearchField } from '../../hooks/useSearchField';

export function OwnerSelectModal({
  onClose,
  openModal,
  currentRadioValue,
  setCurrentRadioValue,
}: Props): React.MixedElement {
  const { __ } = useTranslator();
  const dispatch = useDispatch();
  const location = useLocation();

  const { isReseller, cartOwnerOrganization, productOwnerOrganization } =
    useCartSummaryContext();

  const overviewPage = location.pathname.includes('overview');
  const organizations = useAppSelector(selectOrganizationsList);

  const showOwnerContact = !overviewPage;
  const otherOrganizations = showOwnerContact
    ? organizations.filter(orga => orga.id !== cartOwnerOrganization?.id)
    : organizations;

  const { filteredList, searchTerm, handleFilter } = useSearchField(
    otherOrganizations,
    ['name', 'orgname', 'firstname', 'lastname'],
  );

  const cartCustomer = useAppSelector(selectCartReselleeID);

  const isLoaded = useAppSelector(organizationsResource.selectIsLoaded);

  const shouldFetch = useAppSelector(organizationsResource.selectShouldFetch);

  const isSamlUser = useAppSelector(selectIsSamlUser);

  React.useEffect(() => {
    if (
      otherOrganizations.length === 0 &&
      !cartOwnerOrganization?.reseller &&
      !overviewPage &&
      isLoaded
    ) {
      openModal({
        type: 'createAccountModal',
        replace: true,
      });
    }
  }, [
    openModal,
    cartOwnerOrganization?.reseller,
    overviewPage,
    otherOrganizations.length,
    isLoaded,
  ]);

  React.useEffect(() => {
    if (shouldFetch) {
      dispatch(fetchOrganizationsList());
    }
  }, [dispatch, shouldFetch]);

  const handleChangeOwner = useApplyOrgaProductOwner({
    onSuccess: () => {
      setCurrentRadioValue();
      onClose();
    },
  });

  if (!organizations || organizations.length === 0)
    return <Loader size="medium" ariaLabel="Loading" />;

  const displayedProductOwner = isReseller
    ? cartCustomer || productOwnerOrganization?.id
    : productOwnerOrganization?.id;

  const radioValue =
    currentRadioValue || displayedProductOwner || cartOwnerOrganization?.id;

  function handleEdit(value: string) {
    setCurrentRadioValue(value);
    openModal({
      type: 'editAccountModal',
      orgId: value,
      actionOnSuccess: 'onBack',
    });
  }

  function handleCreateContact() {
    openModal({
      type: 'createAccountModal',
      actionOnSuccess: 'onBack',
      currentRole: 'PRODUCT_OWNER',
    });
  }

  const showFilter = organizations.length > 4;

  const organizationFound = organizations.some(orga => orga.id === radioValue);

  return (
    <>
      {showFilter && <SearchField value={searchTerm} onChange={handleFilter} />}

      {!isSamlUser && (
        <View
          as="div"
          attributes={{ style: { marginBottom: 'var(--rs-unit-x1)' } }}
        >
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link icon={Plus} onClick={handleCreateContact}>
            {__('Create contact')}
          </Link>
        </View>
      )}

      <SelectContactForm
        // Force a remount so that the latest value is selected and the list is up to date after the organization is created.
        key={organizationFound ? 'found' : undefined}
        type="organization"
        initialValue={organizationFound ? radioValue : undefined}
        items={filteredList}
        handleEdit={handleEdit}
        handleSubmit={handleChangeOwner}
        openModal={openModal}
      />
    </>
  );
}
